import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Injector,
  Input,
  OnChanges,
  OnInit
} from '@angular/core';
import { map, startWith } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, defer, merge } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { VariableBenefitConfigurationJurisdictionDetailFormGroup } from '../../../../records/components/members/ip-member-record/components/ip-member-record-variable-benefit-configuration/form-groups/variable-benefit-configuration-jurisdiction-detail-form-group';
import { GoverningJurisdictionTypeId } from '../../../../../models/enum/shared/governing-jurisdiction-type-id';
import { AssetSelectionTypeEnum } from '../../../../../models/enum/shared/asset-selection-type.enum';
import { openUnlockFundsModal } from '../account-asset-selection-unlock-funds-modal/open-unlock-funds-modal';
import { CommonModule } from '@angular/common';
import { CheckboxInputComponent } from '../../../../../ui/form-components/checkbox-input/checkbox-input.component';
import { FormOptionLabelComponent } from '../../../../../ui/form-components/form-option-label/form-option-label.component';
import { CurrencyInputComponent } from '../../../../../ui/form-components/currency-input/currency-input.component';
import { FormErrorComponent } from '../../../../../ui/form-components/form-error/form-error.component';
import {
  effectiveLockedBalance,
  effectiveUnlockedBalance,
  shouldShowCheck,
  wireJurisdictionRowAutoFill
} from '../utils/asset-selection-rules';
import { CreateMonthlyPensionAccountDcJurisdictionDetailFormGroup } from '../../../../records/components/members/ip-member-record/components/ip-member-record-create-monthly-pension-account/form-groups/create-monthly-pension-account-dc-jurisdiction-detail-form-group';
import { FinalLocationJurisdictionTitleComponent } from '../../../../../features/governing-jurisdiction-override/components/final-location-jurisdiction-title/final-location-jurisdiction-title.component';
import { unlockButtonLabel } from '../utils/unlock-button-label';

@Component({
  selector: 'app-account-asset-selection-jurisdiction-row',
  standalone: true,
  templateUrl: './account-asset-selection-jurisdiction-row.component.html',
  styleUrls: ['./account-asset-selection-jurisdiction-row.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CheckboxInputComponent,
    FormOptionLabelComponent,
    CurrencyInputComponent,
    FormErrorComponent,
    FinalLocationJurisdictionTitleComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountAssetSelectionJurisdictionRowComponent implements OnInit, OnChanges {
  @Input({ required: true }) formGroup!:
    | VariableBenefitConfigurationJurisdictionDetailFormGroup
    | CreateMonthlyPensionAccountDcJurisdictionDetailFormGroup;
  @Input({ required: true }) lockedBalance!: number;
  @Input({ required: true }) unlockedBalance!: number;
  @Input() formSubmitted = false;
  /** Whether this row has its own Unlock; a header Unlock covering the row is the host's to show. */
  @Input() showUnlock = true;
  @Input() overrideJurisdictionId: GoverningJurisdictionTypeId | null = null;
  @Input() maxJurisdictions: GoverningJurisdictionTypeId[] = [
    GoverningJurisdictionTypeId.Alberta,
    GoverningJurisdictionTypeId.Ontario
  ];

  private assetSelectionType$ = new BehaviorSubject<AssetSelectionTypeEnum | null>(null);
  @Input()
  set assetSelectionType(value: AssetSelectionTypeEnum | null) {
    this.assetSelectionType$.next(value);
  }

  private destroyRef = inject(DestroyRef);
  private ngbModal = inject(NgbModal);
  private injector = inject(Injector);

  public readonly customLockedAmountErrorMessages = {
    max: $localize`The amount can not exceed the Locked-In value`,
    min: $localize`Amount must be greater than or equal to 0`
  };

  public jurisdictionId!: GoverningJurisdictionTypeId | null;
  public totalBalance = 0;

  private readonly lockedSelectionChanges$ = defer(() => {
    const { desiredLockedStartBalance, useEntireLockedBalance } = this.formGroup.controls;
    return merge(desiredLockedStartBalance.valueChanges, useEntireLockedBalance.valueChanges).pipe(startWith(null));
  });

  private readonly unlockedSelectionChanges$ = defer(() => {
    const { desiredUnlockedStartBalance, useEntireUnlockedBalance } = this.formGroup.controls;
    return merge(desiredUnlockedStartBalance.valueChanges, useEntireUnlockedBalance.valueChanges).pipe(startWith(null));
  });

  private readonly unlockState$ = defer(() => {
    const { lockedAmountToUnlock, lockedPercentageToUnlock } = this.formGroup.controls;
    return combineLatest([
      lockedAmountToUnlock.valueChanges.pipe(startWith(lockedAmountToUnlock.value)),
      lockedPercentageToUnlock.valueChanges.pipe(startWith(lockedPercentageToUnlock.value))
    ]);
  });

  // A type switch resets the desired amounts without emitting, so read them when the type changes too.
  public readonly showLockedCheck$ = combineLatest([this.assetSelectionType$, this.lockedSelectionChanges$]).pipe(
    map(([type]) => shouldShowCheck(type, this.formGroup.controls.desiredLockedStartBalance.value, 'locked'))
  );

  public readonly showUnlockedCheck$ = combineLatest([this.assetSelectionType$, this.unlockedSelectionChanges$]).pipe(
    map(([type]) => shouldShowCheck(type, this.formGroup.controls.desiredUnlockedStartBalance.value, 'unlocked'))
  );

  public readonly hasUnlock$ = this.unlockState$.pipe(
    map(([amount, percentage]) => amount !== null || percentage !== null)
  );

  public readonly effectiveLockedBalance$ = this.unlockState$.pipe(
    map(([amount, percentage]) => effectiveLockedBalance(this.lockedBalance, amount, percentage))
  );

  public readonly effectiveUnlockedBalance$ = this.unlockState$.pipe(
    map(([amount, percentage]) =>
      effectiveUnlockedBalance(this.lockedBalance, this.unlockedBalance, amount, percentage)
    )
  );

  public readonly showUnlockedSection$ = this.effectiveUnlockedBalance$.pipe(map(balance => balance > 0));

  public readonly unlockButtonLabel$ = this.hasUnlock$.pipe(map(unlockButtonLabel));

  public readonly isSpecifyAmount$ = this.assetSelectionType$.pipe(
    map(type => type === AssetSelectionTypeEnum.SpecifyAmount)
  );

  public ngOnInit(): void {
    wireJurisdictionRowAutoFill(this.formGroup, this.lockedBalance, this.unlockedBalance, this.destroyRef);

    // Update locked-in max dynamically based on new locked balance
    this.effectiveLockedBalance$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(effectiveBalance => {
      const control = this.formGroup.controls.desiredLockedStartBalance;
      control.setValidators([Validators.min(0), Validators.max(effectiveBalance)]);
      control.updateValueAndValidity();
    });
  }

  public ngOnChanges(): void {
    this.jurisdictionId = this.formGroup.controls.governingJurisdictionId.value;
    this.totalBalance = (this.lockedBalance ?? 0) + (this.unlockedBalance ?? 0);
  }

  public onUnlockClick(): void {
    openUnlockFundsModal(this.ngbModal, this.injector, {
      finalLocationId: this.overrideJurisdictionId,
      rows: [{ formGroup: this.formGroup, lockedBalance: this.lockedBalance }],
      mode: 'single',
      maxJurisdictions: this.maxJurisdictions
    });
  }
}
